/*
 * PBN Quick add
 * Any button with [data-pbn-quick-add][data-variant-id] adds that variant to the cart
 * without leaving the page, lets Horizon re-render the cart drawer via
 * CartLinesUpdateEvent, then opens the drawer so the shopper sees the bundle progress.
 *
 * Option buttons on product cards ([data-pbn-pick]) switch the card's variant:
 * the "+ Add to Cart" button and the price follow the chosen option.
 *
 * "Complete your kit" row in the cart drawer ([data-pbn-up]): arrows scroll one item
 * left/right, the "1 / 3" counter and arrow states follow swiping. Bound once on the
 * document, so it keeps working after the drawer is re-rendered.
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function openCartDrawer() {
  const component = document.querySelector('cart-drawer-component');
  if (component && typeof component.open === 'function') {
    component.open();
    return;
  }
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.showDialog === 'function') {
    drawer.showDialog();
    return;
  }
  const dialog = drawer && drawer.querySelector('dialog');
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
}

async function quickAdd(button) {
  const label = button.querySelector('[data-label]');
  const original = label ? label.textContent : '';
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (label) label.textContent = 'Adding…';

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  let events = null;
  let deferred = null;
  try {
    events = await import('@shopify/events');
    deferred = events.CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        promise: deferred.promise,
      })
    );
  } catch (e) {
    deferred = null;
  }

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Could not add to cart');

    if (deferred) {
      let cart = null;
      let itemCount;
      try {
        const cartJson = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        itemCount = cartJson.item_count;
        cart = events.CartLinesUpdateEvent.createCartFromAjaxResponse
          ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson)
          : cartJson;
      } catch (e) {}
      deferred.resolve({
        cart,
        detail: { sections: data.sections || undefined, items: data.items, itemCount, source: 'pbn-quick-add', didError: false },
      });
    }

    if (label) label.textContent = 'Added \u2713';
    button.classList.add('is-added');
    setTimeout(openCartDrawer, 250);
    setTimeout(() => {
      if (label) label.textContent = original;
      button.classList.remove('is-added');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }, 2500);

    if (!deferred) window.location.reload();
  } catch (error) {
    console.warn('[pbn-quick-add]', error);
    if (deferred) deferred.reject(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (label) label.textContent = 'Try again';
  }
}

function pickOption(pill) {
  const card = pill.closest('.pbn-row__card');
  if (!card) return;
  card.querySelectorAll('[data-pbn-pick]').forEach((b) => {
    const on = b === pill;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  const add = card.querySelector('[data-pbn-quick-add]');
  if (add) add.dataset.variantId = pill.dataset.variantId;
  const price = card.querySelector('[data-pbn-card-price]');
  if (price && pill.dataset.priceHtml) price.innerHTML = pill.dataset.priceHtml;
}

/* ---- "Complete your kit" swipe row ---- */
function upStep(track) {
  const slide = track.querySelector('.pbn-up__slide');
  if (!slide) return track.clientWidth;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  return slide.getBoundingClientRect().width + gap;
}

function upSync(track) {
  const box = track.closest('[data-pbn-up]');
  if (!box) return;
  const n = track.children.length;
  const step = upStep(track) || 1;
  const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
  const index = atEnd ? n : Math.min(n, Math.round(track.scrollLeft / step) + 1);
  const count = box.querySelector('[data-pbn-up-count]');
  if (count) count.textContent = `${index} / ${n}`;
  const prev = box.querySelector('[data-pbn-up-prev]');
  const next = box.querySelector('[data-pbn-up-next]');
  if (prev) prev.disabled = track.scrollLeft < 4;
  if (next) next.disabled = atEnd;
}

if (!window.__pbnQuickAddBound) {
  window.__pbnQuickAddBound = true;
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-pbn-up-prev], [data-pbn-up-next]');
    if (arrow) {
      event.preventDefault();
      const box = arrow.closest('[data-pbn-up]');
      const track = box && box.querySelector('[data-pbn-up-track]');
      if (track) {
        const dir = arrow.hasAttribute('data-pbn-up-next') ? 1 : -1;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({ left: dir * upStep(track), behavior: reduce ? 'auto' : 'smooth' });
        setTimeout(() => upSync(track), reduce ? 0 : 400);
      }
      return;
    }
    const pill = event.target.closest('[data-pbn-pick]');
    if (pill) {
      if (!pill.disabled) pickOption(pill);
      return;
    }
    const button = event.target.closest('[data-pbn-quick-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    quickAdd(button);
  });

  let upTimer = null;
  document.addEventListener(
    'scroll',
    (event) => {
      const t = event.target;
      if (!t || !t.matches || !t.matches('[data-pbn-up-track]')) return;
      clearTimeout(upTimer);
      upTimer = setTimeout(() => upSync(t), 60);
    },
    true
  );
}
